import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { PremiereProject } from './interfaces';
import { premiere } from '@mimir/web/globals/premiere';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';

export interface ProjectSaveResult {
  success: boolean;
  message?: string;
  error?: Error;
  newVersionId?: string;
}

export interface ProjectMetadata {
  itemId: string | null;
  projectPath: string;
}

/**
 * Utility function to get the currently active project by matching project path
 * This ensures we get the exact project that's highlighted/selected in Adobe,
 * not just the one marked as currently active from our previously opened project from mimir.
 */
export async function getActiveProject(projectPath: string): Promise<PremiereProject> {
  const openProjects = premiere.orderedOpenProjects;

  if (projectPath) {
    const projectByPath = openProjects.find(pr => pr.path === projectPath);
    if (projectByPath) {
      return projectByPath;
    } else {
      console.warn('getActiveProject: Provided project path not found in open projects:', projectPath);
    }
  }

  // Fallback to the traditional way - get currently active project
  const activeProject = openProjects.find(pr => pr.isCurrentlyActive);
  console.log('getActiveProject: Fallback to isCurrentlyActive:', activeProject?.path);

  if (!activeProject) {
    throw new Error('No active project found');
  }

  return activeProject;
}

/**
 * Updates the Mimir item ID in ALL open projects that currently have the same original item ID.
 * This ensures that if the same project is opened multiple times, all copies stay in sync
 * when a new version is created.
 */
export async function setAllRelatedMimirItemId(
  applicationContext: AdobePanelContext | ElectronContext,
  oldItemId: string,
  newItemId: string
): Promise<void> {
  try {
    await applicationContext.cepBridgeClient.call('updateAllRelatedProjectsMetadata', oldItemId, newItemId);
  } catch (error) {
    logErrorAndShowToast('Failed to update all related project metadata', error);
  }
}

/**
 * Stores the Mimir item ID in the currently active project's root bin metadata.
 */
export async function setCurrentProjectMimirItemIdMetadata(
  applicationContext: AdobePanelContext | ElectronContext,
  itemId: string
): Promise<void> {
  try {
    await applicationContext.cepBridgeClient.call('setCurrentActiveProjectMetadata', itemId);
  } catch (error) {
    logErrorAndShowToast('Failed to set Mimir item ID in currently active project metadata', error);
  }
}

/**
 * Retrieves the original Mimir item metadata from the currently active project's root bin metadata.
 * Returns both itemId and projectPath for comprehensive project identification.
 */
export async function getCurrentProjectMimirItemIdMetadata(
  applicationContext: AdobePanelContext | ElectronContext,
): Promise<ProjectMetadata> {
  try {
    return await applicationContext.cepBridgeClient.call('getCurrentActiveProjectMetadata');
  } catch (error) {
    logErrorAndShowToast('Failed to get Mimir item metadata from currently active project', error);
    return {
      itemId: null,
      projectPath: '',
    };
  }
}
